import { useState } from 'react';
import { CaretRightIcon, ListChecksIcon } from '@phosphor-icons/react';

import classNames from '@/utils/classnames';

import { useAgentSessionStore } from '../../stores/agent-session-store';
import { summarizeTodos } from '../../stores/todos';

import { TodoChecklist } from './TodoToolCard';

/**
 * Compact, always-visible todo progress pinned above the thread. Tracks the store's latest
 * `set_todos` state so the user can see remaining work without scrolling to find the last
 * checklist. Expands to the full list on click.
 */
export function TodoProgressIndicator() {
  const todos = useAgentSessionStore((s) => s.todos);
  const [open, setOpen] = useState(false);

  if (todos.length === 0) return null;

  const { total, completed } = summarizeTodos(todos);
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  const allDone = completed === total;

  return (
    <div className="shrink-0 border-b border-line-subtle bg-surface-1 px-6 py-2.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 text-left"
      >
        <ListChecksIcon
          size={15}
          weight="bold"
          className={classNames('shrink-0', allDone ? 'text-success' : 'text-brand-text')}
        />
        <span className="shrink-0 text-xs font-medium text-fg tabular-nums">
          {completed} of {total} todos done
        </span>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3">
          <div
            className={classNames(
              'h-full rounded-full transition-[width] duration-240',
              allDone ? 'bg-success' : 'bg-brand',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
        <CaretRightIcon
          size={12}
          weight="bold"
          className={classNames('shrink-0 text-fg-tertiary transition-transform', open && 'rotate-90')}
        />
      </button>
      {open && (
        <div className="pt-2.5">
          <TodoChecklist todos={todos} />
        </div>
      )}
    </div>
  );
}
